import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  MessageCircle, Shield, Users, Scale, Brain,
  TrendingUp, Target, Clock, Play, Building2, Briefcase, UserCheck,
  Sparkles, Globe
} from 'lucide-react';

const ICON_MAP = { MessageCircle, Shield, Users, Scale, Brain };

const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'ta', name: 'Tamil', native: 'தமிழ்' },
  { code: 'te', name: 'Telugu', native: 'తెలుగు' },
  { code: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml', name: 'Malayalam', native: 'മലയാളം' },
  { code: 'mr', name: 'Marathi', native: 'मराठी' },
  { code: 'bn', name: 'Bengali', native: 'বাংলা' },
  { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
];

const CATEGORY_COLORS = {
  'Communication': { bg: 'bg-accent/10', text: 'text-accent', border: 'border-accent/20' },
  'Leadership': { bg: 'bg-brand/10', text: 'text-brand', border: 'border-brand/20' },
  'Strategy': { bg: 'bg-info/10', text: 'text-info', border: 'border-info/20' },
  'Negotiation': { bg: 'bg-warning/10', text: 'text-warning', border: 'border-warning/20' },
  'Crisis Management': { bg: 'bg-destructive/10', text: 'text-destructive', border: 'border-destructive/20' },
  'Customer Relations': { bg: 'bg-success/10', text: 'text-success', border: 'border-success/20' },
  'Custom': { bg: 'bg-muted', text: 'text-subtle', border: 'border-border' },
};

export default function Dashboard() {
  const { user, axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [progress, setProgress] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState({});
  const [selectedVariation, setSelectedVariation] = useState({});
  const [startingId, setStartingId] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupScenario, setSetupScenario] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [setupForm, setSetupForm] = useState({
    user_role: '', partner_name: '', partner_role: '', department: '', scenario_context: ''
  });

  useEffect(() => {
    const api = axiosAuth();
    Promise.all([api.get('/scenarios'), api.get('/progress')])
      .then(([scenRes, progRes]) => {
        setScenarios(scenRes.data.scenarios);
        setProgress(progRes.data);
      }).catch(() => toast.error('Failed to load data'));
  }, [axiosAuth]);

  const openSetup = (scenario) => {
    setSetupScenario(scenario);
    setSetupForm({ user_role: '', partner_name: scenario.avatar_name || '', partner_role: '', department: '', scenario_context: '' });
    setSetupOpen(true);
  };

  const startSimulation = async () => {
    if (!setupScenario) return;
    const scenarioId = setupScenario.id;
    setStartingId(scenarioId);
    setSetupOpen(false);
    try {
      const api = axiosAuth();
      const res = await api.post('/simulations/start', {
        scenario_id: scenarioId,
        difficulty: selectedDifficulty[scenarioId] || 'intermediate',
        variation: selectedVariation[scenarioId] || 'default',
        language: selectedLanguage,
        ...setupForm,
      });
      navigate(`/simulation/${res.data.simulation.id}`);
    } catch (err) {
      const isTimeout = err.response?.status === 504;
      if (isTimeout) {
        toast.error('The AI is taking too long to respond. Please try again.', {
          duration: 8000,
          action: { label: 'Retry', onClick: () => startSimulation() },
        });
      } else {
        toast.error(err.response?.data?.detail || 'Failed to start simulation');
      }
    } finally { setStartingId(null); }
  };

  const stats = [
    { label: 'Completed', value: progress?.completed || 0, icon: Target, color: 'text-success' },
    { label: 'In Progress', value: progress?.in_progress || 0, icon: Clock, color: 'text-warning' },
    { label: 'Avg Score', value: progress?.overall_average_score ? `${progress.overall_average_score}/10` : '--', icon: TrendingUp, color: 'text-brand' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-1">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="font-body text-sm text-muted-foreground">
            Select a scenario and difficulty to begin your simulation.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
          {stats.map((s, i) => (
            <div key={i} className="heritage-card p-4 sm:p-5" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
              <div className="flex items-center gap-2 mb-1">
                <s.icon className={`w-4 h-4 ${s.color}`} />
                <span className="text-[11px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">{s.label}</span>
              </div>
              <p className="font-heading text-xl sm:text-2xl font-bold">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Scenarios header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-lg sm:text-xl font-bold">Choose Your Simulation</h2>
          <Badge className="bg-muted text-muted-foreground border border-border font-body text-xs">
            {scenarios.length} available
          </Badge>
        </div>

        {/* Scenario cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map((scenario, idx) => {
            const IconComp = ICON_MAP[scenario.icon] || Brain;
            const catColor = CATEGORY_COLORS[scenario.category] || CATEGORY_COLORS['Custom'];
            const variations = scenario.variations || [];

            return (
              <div key={scenario.id}
                className="heritage-card overflow-hidden animate-slide-up"
                style={{ animationDelay: `${idx * 0.04}s`, opacity: 0, animationFillMode: 'forwards' }}
                data-testid={`scenario-card-${scenario.id}`}>

                {/* Card header with illustration */}
                <div className="relative h-32 sm:h-36 overflow-hidden" style={{ backgroundColor: `${scenario.color || 'var(--brand)'}10` }}>
                  {scenario.avatar ? (
                    <img src={scenario.avatar} alt={scenario.title}
                      className="w-full h-full object-contain p-4" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <IconComp className="w-14 h-14 opacity-20" style={{ color: scenario.color || 'var(--brand)' }} />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap">
                    <Badge className={`${catColor.bg} ${catColor.text} border ${catColor.border} font-body text-[11px] px-2 py-0`}>
                      {scenario.category}
                    </Badge>
                    {scenario.is_custom && (
                      <Badge className="bg-warning/10 text-warning border border-warning/20 font-body text-[11px] px-2 py-0">Custom</Badge>
                    )}
                    {scenario.industry && (
                      <Badge className="bg-muted text-muted-foreground border border-border font-body text-[11px] px-2 py-0">{scenario.industry}</Badge>
                    )}
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  <h3 className="font-heading text-base font-bold mb-1.5 leading-tight">{scenario.title}</h3>
                  <p className="font-body text-xs sm:text-sm text-muted-foreground mb-3 leading-relaxed line-clamp-2">
                    {scenario.description}
                  </p>

                  <div className="flex flex-wrap gap-1 mb-3">
                    {(scenario.skills || []).slice(0, 3).map(skill => (
                      <span key={skill} className="font-body text-[11px] bg-muted text-muted-foreground rounded-full px-2 py-0.5">
                        {skill}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 mb-3 font-body text-[11px] text-muted-foreground">
                    <Clock className="w-3 h-3" /> {scenario.estimated_time}
                    <span className="mx-0.5 opacity-30">|</span>
                    <span>{variations.length} variation{variations.length !== 1 ? 's' : ''}</span>
                  </div>

                  {variations.length > 1 && (
                    <div className="mb-3">
                      <Select value={selectedVariation[scenario.id] || 'default'}
                        onValueChange={(val) => setSelectedVariation(prev => ({ ...prev, [scenario.id]: val }))}>
                        <SelectTrigger data-testid={`variation-select-${scenario.id}`}
                          className="w-full border border-border h-8 font-body text-xs bg-surface">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="border border-border shadow-modal bg-surface">
                          {variations.map(v => (
                            <SelectItem key={v.id} value={v.id} className="font-body text-xs">{v.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Select value={selectedDifficulty[scenario.id] || 'intermediate'}
                      onValueChange={(val) => setSelectedDifficulty(prev => ({ ...prev, [scenario.id]: val }))}>
                      <SelectTrigger data-testid={`difficulty-select-${scenario.id}`}
                        className="flex-1 border border-border h-8 font-body text-xs bg-surface">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border border-border shadow-modal bg-surface">
                        <SelectItem value="beginner" className="text-xs">Beginner</SelectItem>
                        <SelectItem value="intermediate" className="text-xs">Intermediate</SelectItem>
                        <SelectItem value="advanced" className="text-xs">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button data-testid={`start-simulation-${scenario.id}`}
                      onClick={() => openSetup(scenario)}
                      disabled={startingId === scenario.id}
                      className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90">
                      {startingId === scenario.id ? (
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <><Play className="w-3 h-3" /> Start</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Setup Dialog */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-md" data-testid="setup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Set Up Your Scenario</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">
              Tell us about yourself and the situation for a contextually relevant simulation.
            </DialogDescription>
          </DialogHeader>

          {setupScenario && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
                <Play className="w-4 h-4 text-brand" />
                <div>
                  <p className="font-heading text-sm font-bold">{setupScenario.title}</p>
                  <p className="font-body text-xs text-muted-foreground">
                    {selectedDifficulty[setupScenario.id] || 'intermediate'} difficulty
                  </p>
                </div>
              </div>

              {/* Language Selector */}
              <div>
                <label className="font-body text-sm font-medium mb-1.5 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-subtle" /> Conversation Language
                </label>
                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <SelectTrigger data-testid="language-select" className="w-full border border-border h-9 font-body text-sm bg-surface">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border border-border shadow-modal bg-surface max-h-60">
                    {LANGUAGES.map(l => (
                      <SelectItem key={l.code} value={l.code} className="font-body text-sm">
                        {l.native} <span className="text-muted-foreground ml-1">({l.name})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedLanguage !== 'en' && (
                  <p className="font-body text-[10px] text-muted-foreground mt-1">AI will converse in {LANGUAGES.find(l => l.code === selectedLanguage)?.native}. Voice input & output will also use this language.</p>
                )}
              </div>

              <div>
                <label className="font-body text-sm font-medium mb-1.5 flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5 text-subtle" /> Your Role / Title
                </label>
                <input data-testid="setup-user-role" type="text" value={setupForm.user_role}
                  onChange={(e) => setSetupForm(prev => ({ ...prev, user_role: e.target.value }))}
                  className="heritage-input w-full text-sm" placeholder="e.g., Engineering Manager" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-body text-sm font-medium mb-1.5 flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-subtle" /> Partner Name
                  </label>
                  <input data-testid="setup-partner-name" type="text" value={setupForm.partner_name}
                    onChange={(e) => setSetupForm(prev => ({ ...prev, partner_name: e.target.value }))}
                    className="heritage-input w-full text-sm" placeholder="e.g., Rahul, Sarah" />
                </div>
                <div>
                  <label className="font-body text-sm font-medium mb-1.5 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-subtle" /> Partner Role
                  </label>
                  <input data-testid="setup-partner-role" type="text" value={setupForm.partner_role}
                    onChange={(e) => setSetupForm(prev => ({ ...prev, partner_role: e.target.value }))}
                    className="heritage-input w-full text-sm" placeholder="e.g., Senior Developer" />
                </div>
              </div>

              <div>
                <label className="font-body text-sm font-medium mb-1.5 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-subtle" /> Department
                  <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                </label>
                <input data-testid="setup-department" type="text" value={setupForm.department}
                  onChange={(e) => setSetupForm(prev => ({ ...prev, department: e.target.value }))}
                  className="heritage-input w-full text-sm" placeholder="e.g., Engineering, Sales, Marketing" />
              </div>

              <div>
                <label className="font-body text-sm font-medium mb-1.5 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-subtle" /> Scenario Context
                  <span className="text-xs text-muted-foreground font-normal">(sets the scene)</span>
                </label>
                <textarea data-testid="setup-scenario-context" value={setupForm.scenario_context}
                  onChange={(e) => setSetupForm(prev => ({ ...prev, scenario_context: e.target.value }))}
                  className="heritage-input w-full text-sm min-h-[70px]" rows={3}
                  placeholder="Describe the situation... e.g., Rahul has been consistently missing deadlines for the last 3 sprints. You need to address this in a one-on-one meeting. He is usually a strong performer but seems distracted lately." />
                <p className="font-body text-[10px] text-muted-foreground mt-1">The partner will start the conversation based on this context</p>
              </div>

              <div className="flex gap-2 pt-1">
                <Button data-testid="setup-skip-button" onClick={() => {
                    setSetupForm({ user_role: '', partner_name: setupScenario?.avatar_name || '', partner_role: '', department: '', scenario_context: '' });
                    startSimulation();
                  }}
                  className="flex-1 h-10 bg-transparent border border-border text-foreground hover:bg-muted text-sm font-medium rounded-lg shadow-none">
                  Skip & Start
                </Button>
                <Button data-testid="setup-start-button" onClick={startSimulation}
                  className="flex-1 h-10 bg-brand text-[#333333] gap-1.5 text-sm font-medium rounded-lg hover:opacity-90">
                  <Play className="w-4 h-4" /> Begin
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
